import { useEffect } from 'react';
import { graphql } from 'react-relay';

import { useSubscriptionInterop } from '@confluence/apollo-relay-interop/entry-points/useSubscriptionInterop.ts';
import { useSubscriptionValues } from '@confluence/graphql/entry-points/useSubscriptionValues.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import type {
	ContentTypesTitleSubscription$data,
	ContentTypesTitleSubscription$variables,
} from './__generated__/ContentTypesTitleSubscription.graphql.ts';

export const errorStringsToIgnore = [
	'401',
	'Failed to fetch',
	'Load Failed',
	'Network error',
	'GraphQL error: undefined',
];

export const onContentTypesTitleSubscriptionData = (
	response: any,
	onDataReceived: (contentState: any) => void,
) => {
	const payload = response?.subscriptionData?.data?.confluence_onContentModified;
	const _deltas = payload?._deltas;
	if (!payload || !Array.isArray(_deltas)) {
		return;
	}
	if (_deltas?.length === 0 || _deltas.indexOf('contentTitleUpdated') === -1) {
		return;
	}
	const { contentTitleUpdated } = payload;

	const title = contentTitleUpdated?.contentTitle;
	onDataReceived(title);
};

export const ContentTypesTitleSubscription = ({
	contentType,
	contentId,
	onDataReceived,
	skip = false,
}: {
	contentType: 'page' | 'blogpost';
	contentId: string;
	onDataReceived: (title: any) => void;
	skip?: boolean;
}) => {
	const { id, onSubscriptionComplete, extensions, onSubscriptionData } = useSubscriptionValues({
		contentType,
		contentId,
	});
	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	const { error } = useSubscriptionInterop<
		ContentTypesTitleSubscription$data,
		ContentTypesTitleSubscription$variables
	>(
		graphql`
			subscription ContentTypesTitleSubscription($id: ID!) {
				confluence_onContentModified(id: $id) {
					type
					id
					__typename
					_deltas
					contentTitleUpdated {
						contentTitle
						__typename
					}
				}
			}
		`,
		{
			variables: {
				id,
			},
			extensions,
			onSubscriptionComplete,
			onSubscriptionData(response) {
				onContentTypesTitleSubscriptionData(response, onDataReceived);
				onSubscriptionData(response);
			},
			skip: skip || !id || skipSubscriptionForAnonUsers,
			interopApolloAggClient: true,
		},
	);

	useEffect(() => {
		if (error) {
			// if there is no error message it is likely to be a client side network issue
			// eg spotty vpn
			const errorMessage =
				error?.message ||
				(error as any)?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.originalError?.message ||
				error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.extensions?.message;

			const isIgnoredError =
				errorStringsToIgnore.some((errorString) => error?.message.includes(errorString)) ||
				!errorMessage;
			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (statusCode !== 403 && !isIgnoredError) {
				const e = new Error(`Subscription Error - ContentTypesTitleSubscription:${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
			}
			markErrorAsHandled(error);
		}
	}, [error, contentId, contentType]);

	return null;
};
